// src/tattoo/components/QuotePoolCta.jsx
import React, { useState } from "react";
import { createQuoteRequest } from "../lib/supabaseClient";
import { getOrCreateShareUrlForCurrentProject } from "../share";

export default function QuotePoolCta({
  renderedImageUrls = [],
  placementUrl,
  onRefreshPreview,
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [stylesText, setStylesText] = useState("");
  const [budgetText, setBudgetText] = useState("");
  const [availabilityText, setAvailabilityText] = useState("");
  const [note, setNote] = useState("");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);

  const canSubmit = !loading && !!email;

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 1) Refresh preview PNG if possible
      let images = renderedImageUrls;
      if (onRefreshPreview) {
        const refreshed = await onRefreshPreview({
          width: 1600,
          height: 1600,
          format: "image/png",
        });
        if (Array.isArray(refreshed) && refreshed.length) {
          images = refreshed;
        }
      }

      // 2) Build interactive /view link (same pattern as TestLeadForm)
    // 2) Build a *real* interactive /view link using the share pipeline
     let shareUrl = "";
     try {
       if (typeof window !== "undefined") {
         // uses exportProjectHashAsync + makeShareLinkResilient under the hood
         shareUrl = await getOrCreateShareUrlForCurrentProject({ r: true });
       }
     } catch (e) {
        console.warn("[quote] failed to build shareUrl, falling back:", e);
      // optional tiny fallback, just in case
       if (typeof window !== "undefined") {
         const { origin, hash } = window.location;
         shareUrl = `${origin}/view${hash || ""}`;
       }
     }

      // 3) Normalize styles
      const styleTags = stylesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const payload = {
        userName: name,
        userEmail: email,
        userCity: city,
        userCountry: country,
        styleTags,
        budgetText,
        availabilityText,
        note,
        designImages: images,
        placementImage: placementUrl || undefined,
        shareUrl,
      };

      const res = await createQuoteRequest(payload);
      setViewUrl(res?.viewUrl || null);
      // Prefer the same URL the artists see (interactive) for the little success card
      setViewUrl(shareUrl || res?.viewUrl || null);
      setSuccess(true);
    } catch (err) {
      console.error("[quote] submit error", err);
      setError(
        err?.message || "Something went wrong sending your quote request."
      );
    } finally {
      setLoading(false);
    }
  }

  // ---------- success view ----------
  if (success) {
    return (
      <div className="tt-lead-card">
        <h3 className="tt-lead-title">Quote request live ✅</h3>
        <p className="tt-lead-subtitle">
          Your design has been shared with interested artists. They’ll email you
          directly if they’re a good fit.
        </p>

        {viewUrl && (
          <div className="tt-lead-success-link">
            <div>You can view this request here:</div>
            <a href={viewUrl} target="_blank" rel="noreferrer">
              {viewUrl}
            </a>
          </div>
        )}
      </div>
    );
  }

  // ---------- form view ----------
  return (
    <form onSubmit={handleSubmit} className="tt-lead-form">
      <div className="tt-lead-card" style={{ marginTop: 8 }}>
        <h3 className="tt-lead-title">Get quotes from Tattune artists</h3>
        <p className="tt-lead-subtitle">
          We’ll share this design with opted-in artists. They can email you
          offers directly.
        </p>

        {/* name + email */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1.4fr",
            gap: 8,
            marginTop: 8,
          }}
        >
          <input
            className="tt-lead-input"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="tt-lead-input"
            type="email"
            placeholder="Your email (required)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        {/* city + country */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1.2fr",
            gap: 8,
            marginTop: 8,
          }}
        >
          <input
            className="tt-lead-input"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <input
            className="tt-lead-input"
            placeholder="Country / region"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>

        {/* styles / budget / availability */}
        <input
          className="tt-lead-input"
          style={{ marginTop: 8 }}
          placeholder="Preferred styles (comma-separated: linework, neo-traditional…)"
          value={stylesText}
          onChange={(e) => setStylesText(e.target.value)}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 8,
          }}
        >
          <input
            className="tt-lead-input"
            placeholder="Budget (e.g. $300–500, open…)"
            value={budgetText}
            onChange={(e) => setBudgetText(e.target.value)}
          />
          <input
            className="tt-lead-input"
            placeholder="When are you available?"
            value={availabilityText}
            onChange={(e) => setAvailabilityText(e.target.value)}
          />
        </div>

        {/* notes */}
        <textarea
          className="tt-lead-textarea"
          style={{ marginTop: 8 }}
          placeholder="Anything else artists should know? (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {error && (
          <p className="tt-lead-error" style={{ marginTop: 4 }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="tt-lead-submit"
          style={{ marginTop: 8 }}
        >
          {loading ? "Posting…" : "Post to artist pool"}
        </button>

        <p
          style={{
            marginTop: 6,
            fontSize: 11,
            opacity: 0.65,
            lineHeight: 1.4,
          }}
        >
          Artists will reply by email if they’re interested. Your email is only
          shared with them for this request.
        </p>
      </div>
    </form>
  );
}